import React, { useEffect, useState } from 'react';
import { shopifyAPI } from '../services/api';
import { useTenantUrl } from '../hooks/useTenantUrl';

// Custom Tooltip component
interface TooltipData {
  month: string;
  year: number;
  type: 'orders' | 'transactions';
  orderCount: number;
  transactionCount: number;
  totalPayments: number;
  totalRefunds: number;
  netAmount: number;
}

const ChartTooltip: React.FC<{
  data: TooltipData | null;
  position: { x: number; y: number } | null;
}> = ({ data, position }) => {
  if (!data || !position) return null;

  const isOrders = data.type === 'orders';

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%)',
        marginTop: '-12px',
        background: 'var(--color-slate-900)',
        color: 'white',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
        minWidth: '200px',
        pointerEvents: 'none',
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      {/* Arrow */}
      <div
        style={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '8px solid var(--color-slate-900)',
        }}
      />

      {/* Header */}
      <div style={{ 
        fontSize: 'var(--text-sm)',
        fontWeight: 700,
        marginBottom: 'var(--space-3)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '2px',
          background: isOrders 
            ? 'linear-gradient(135deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)'
            : 'linear-gradient(135deg, #60a5fa 0%, var(--color-info) 100%)',
        }} />
        {data.month} {data.year}
      </div>

      {/* Content based on type */}
      {isOrders ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-xs)' }}>Orders</span>
            <span style={{ 
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              fontSize: 'var(--text-base)',
              color: 'var(--color-primary-300)',
            }}>
              {data.orderCount.toLocaleString()}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-xs)' }}>Transactions</span>
            <span style={{ 
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              color: '#93c5fd',
            }}>
              {data.transactionCount.toLocaleString()}
            </span>
          </div>
          <div style={{ 
            height: '1px', 
            background: 'rgba(255,255,255,0.1)',
            margin: 'var(--space-1) 0',
          }} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-xs)' }}>Payments</span>
            <span style={{ 
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
              color: '#86efac',
              fontSize: 'var(--text-sm)',
            }}>
              ${Math.round(data.totalPayments).toLocaleString()}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-xs)' }}>Refunds</span>
            <span style={{ 
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
              color: '#fca5a5',
              fontSize: 'var(--text-sm)',
            }}>
              ${Math.round(data.totalRefunds).toLocaleString()}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 'var(--space-2)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}>
            <span style={{ color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600 }}>Net</span>
            <span style={{ 
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              color: 'white',
            }}>
              ${Math.round(data.netAmount).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Action hint */}
      <div style={{
        marginTop: 'var(--space-3)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-neutral-400)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 3h6v6"/>
          <path d="M10 14L21 3"/>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        </svg>
        Click to import this month
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { tenantNavigate } = useTenantUrl();
  const [monthlyStats, setMonthlyStats] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statsLoading, setStatsLoading] = useState(true);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    fetchMonthlyStats();
  }, [selectedYear]);

  const fetchMonthlyStats = async () => {
    try {
      setStatsLoading(true);
      const response = await shopifyAPI.getMonthlyStats(selectedYear);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      type MonthData = {
        transaction_count: string;
        order_count: string;
        total_payments: string;
        total_refunds: string;
        net_amount: string;
      };
      
      const statsMap = new Map<number, MonthData>(
        response.data.data.map((item: any) => [
          parseInt(item.month), 
          {
            transaction_count: item.transaction_count,
            order_count: item.order_count,
            total_payments: item.total_payments,
            total_refunds: item.total_refunds,
            net_amount: item.net_amount,
          }
        ])
      );
      
      const fullYearStats = monthNames.map((name, index) => {
        const monthNum = index + 1;
        const data = statsMap.get(monthNum);
        return {
          month: name,
          transaction_count: data ? parseInt(data.transaction_count) : 0,
          order_count: data ? parseInt(data.order_count) : 0,
          total_payments: data ? parseFloat(data.total_payments) : 0,
          total_refunds: data ? parseFloat(data.total_refunds) : 0,
          net_amount: data ? parseFloat(data.net_amount) : 0,
        };
      });
      
      setMonthlyStats(fullYearStats);
    } catch (err: any) {
      console.error('Failed to load monthly stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleMonthClick = (monthIndex: number) => {
    const year = selectedYear;
    const month = monthIndex + 1;
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    tenantNavigate(`/sync?start_date=${startDate}&end_date=${endDate}`);
  };

  const handleBarHover = (
    e: React.MouseEvent, 
    stat: any, 
    type: 'orders' | 'transactions'
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
    setTooltipData({
      month: stat.month,
      year: selectedYear,
      type,
      orderCount: stat.order_count,
      transactionCount: stat.transaction_count,
      totalPayments: stat.total_payments,
      totalRefunds: stat.total_refunds,
      netAmount: stat.net_amount,
    });
  };

  const handleBarLeave = () => {
    setTooltipData(null);
    setTooltipPosition(null);
  };

  // Calculate totals from monthly stats
  const totalOrders = monthlyStats.reduce((sum, s) => sum + s.order_count, 0);
  const totalTransactions = monthlyStats.reduce((sum, s) => sum + s.transaction_count, 0);
  const totalPayments = monthlyStats.reduce((sum, s) => sum + s.total_payments, 0);
  const totalRefunds = monthlyStats.reduce((sum, s) => sum + s.total_refunds, 0);
  const netAmount = monthlyStats.reduce((sum, s) => sum + s.net_amount, 0);

  const StatCard = ({ title, value, subvalue, icon, color }: { 
    title: string; 
    value: string | number; 
    subvalue: string;
    icon: React.ReactNode;
    color?: string;
  }) => (
    <div className="stat-card" style={{ position: 'relative' }}>
      <div style={{ 
        position: 'absolute', 
        top: 'var(--space-4)', 
        right: 'var(--space-4)',
        width: '40px',
        height: '40px',
        borderRadius: 'var(--radius-lg)',
        background: color ? `${color}15` : 'var(--color-neutral-100)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color || 'var(--color-neutral-500)'
      }}>
        {icon}
      </div>
      <h3>{title}</h3>
      <div className="value" style={{ color: color || 'var(--color-neutral-900)' }}>
        {value}
      </div>
      <div className="subvalue">{subvalue}</div>
    </div>
  );

  return (
    <div>
      {/* Custom Tooltip */}
      <ChartTooltip data={tooltipData} position={tooltipPosition} />

      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your Shopify accounting data for {selectedYear}</p>
      </div>

      {/* Summary Stats */}
      <div className="stat-grid">
        <StatCard 
          title="Total Orders"
          value={totalOrders.toLocaleString()}
          subvalue={`${selectedYear}`}
          color="var(--color-primary-600)"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          }
        />

        <StatCard 
          title="Total Transactions"
          value={totalTransactions.toLocaleString()}
          subvalue="Payments & refunds"
          color="var(--color-info)"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          }
        />

        <StatCard 
          title="Cash Received"
          value={`$${Math.round(totalPayments).toLocaleString()}`}
          subvalue="Total payments"
          color="var(--color-success)"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          }
        />

        <StatCard 
          title="Cash Returned"
          value={`$${Math.round(totalRefunds).toLocaleString()}`}
          subvalue="Total refunds"
          color="var(--color-error)"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
          }
        />

        <StatCard 
          title="Net Revenue"
          value={`$${Math.round(netAmount).toLocaleString()}`}
          subvalue="After refunds"
          color="var(--color-primary-600)"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="20" x2="12" y2="10"/>
              <line x1="18" y1="20" x2="18" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="16"/>
            </svg>
          }
        />
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 'var(--space-6)' 
        }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-600)" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Monthly Overview
          </h2>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setSelectedYear(selectedYear - 1)}
              style={{ padding: 'var(--space-2) var(--space-4)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              {selectedYear - 1}
            </button>
            <span style={{ 
              fontWeight: 700, 
              minWidth: '4.5rem', 
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-lg)',
              color: 'var(--color-neutral-800)'
            }}>
              {selectedYear}
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => setSelectedYear(selectedYear + 1)}
              disabled={selectedYear >= new Date().getFullYear()}
              style={{ padding: 'var(--space-2) var(--space-4)' }}
            >
              {selectedYear + 1}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>

        {statsLoading ? (
          <div className="loading">Loading monthly statistics...</div>
        ) : (
          <>
            {/* Legend */}
            <div style={{ 
              display: 'flex', 
              gap: 'var(--space-6)', 
              marginBottom: 'var(--space-5)', 
              fontSize: 'var(--text-sm)',
              color: 'var(--color-neutral-600)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ 
                  width: '14px', 
                  height: '14px', 
                  background: 'linear-gradient(135deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)',
                  borderRadius: 'var(--radius-sm)' 
                }}></div>
                <span style={{ fontWeight: 500 }}>Orders (by order date)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ 
                  width: '14px', 
                  height: '14px', 
                  background: 'linear-gradient(135deg, #60a5fa 0%, var(--color-info) 100%)',
                  borderRadius: 'var(--radius-sm)' 
                }}></div>
                <span style={{ fontWeight: 500 }}>Transactions (by transaction date)</span>
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(12, 1fr)', 
              gap: 'var(--space-3)',
              minHeight: '320px',
              alignItems: 'flex-end',
              borderBottom: '2px solid var(--color-neutral-200)',
              paddingBottom: 'var(--space-3)',
              marginBottom: 'var(--space-2)'
            }}>
              {monthlyStats.map((stat, idx) => {
                const maxValue = Math.max(
                  ...monthlyStats.map(s => Math.max(s.transaction_count, s.order_count)), 
                  1
                );
                const transactionHeight = (stat.transaction_count / maxValue) * 100;
                const orderHeight = (stat.order_count / maxValue) * 100;
                
                return (
                  <div key={idx} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                  }}>
                    <div style={{ 
                      position: 'relative',
                      width: '100%',
                      height: '220px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      gap: '3px'
                    }}>
                      {/* Orders bar (green) */}
                      <div
                        onClick={() => handleMonthClick(idx)}
                        onMouseEnter={(e) => handleBarHover(e, stat, 'orders')}
                        onMouseLeave={handleBarLeave}
                        style={{
                          width: '44%',
                          height: `${orderHeight}%`,
                          minHeight: stat.order_count > 0 || stat.transaction_count > 0 ? '4px' : '24px',
                          background: stat.order_count > 0 
                            ? 'linear-gradient(180deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)'
                            : 'var(--color-neutral-200)',
                          borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          position: 'relative',
                          boxShadow: stat.order_count > 0 ? '0 2px 8px rgb(16 185 129 / 0.2)' : 'none'
                        }}
                      >
                        {stat.order_count > 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '-22px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            color: 'var(--color-primary-600)'
                          }}>
                            {stat.order_count.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {/* Transactions bar (blue) */}
                      <div
                        onClick={() => handleMonthClick(idx)}
                        onMouseEnter={(e) => handleBarHover(e, stat, 'transactions')}
                        onMouseLeave={handleBarLeave}
                        style={{
                          width: '44%',
                          height: `${transactionHeight}%`,
                          minHeight: stat.order_count > 0 || stat.transaction_count > 0 ? '4px' : '24px',
                          background: stat.transaction_count > 0 
                            ? 'linear-gradient(180deg, #60a5fa 0%, var(--color-info) 100%)'
                            : 'var(--color-neutral-200)',
                          borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          position: 'relative',
                          boxShadow: stat.transaction_count > 0 ? '0 2px 8px rgb(59 130 246 / 0.2)' : 'none'
                        }}
                      >
                        {stat.transaction_count > 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '-22px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            color: 'var(--color-info)'
                          }}>
                            {stat.transaction_count.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: 'var(--text-xs)', 
                      fontWeight: 600,
                      color: 'var(--color-neutral-500)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em'
                    }}>
                      {stat.month}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
